"use client"

import { Flame } from "lucide-react"
import { useTranslations } from "next-intl"
import type React from "react"

/**
 * La etiqueta «¡Quedan pocas!» del kiosko y del menú en línea.
 *
 * Llamativa a propósito (es una advertencia que empuja a pedir YA): degradado
 * rojo→naranja de marca, llama y un pulso suave. Los colores van fijos y no con el
 * acento del tema: sobre el tema de cada restaurante tiene que leerse igual como
 * aviso, y un tema con acento verde o azul la volvería un adorno más.
 * El pulso respeta prefers-reduced-motion (ver .low-stock-pulse en globals.css).
 *
 * `size`: "lg" sobre la foto de la tarjeta, "md" en la fila de chips, "sm" junto a
 * un sabor en los selectores.
 */
export function LowStockBadge({ size = "md", className = "" }: { size?: "sm" | "md" | "lg"; className?: string }) {
  const t = useTranslations("public.lowStock")
  const dims = {
    sm: { fs: 10.5, px: 7, py: 2, icon: 11, gap: 3 },
    md: { fs: 12, px: 9, py: 3, icon: 13, gap: 4 },
    lg: { fs: 13.5, px: 11, py: 5, icon: 15, gap: 5 },
  }[size]
  return (
    <span
      className={`low-stock-pulse ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: dims.gap,
        padding: `${dims.py}px ${dims.px}px`,
        borderRadius: 999,
        background: "linear-gradient(135deg, #D7263D 0%, #F4722B 100%)",
        color: "#fff",
        fontSize: dims.fs,
        fontWeight: 800,
        letterSpacing: "0.01em",
        lineHeight: 1.1,
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(215,38,61,0.35)",
      }}
    >
      <Flame size={dims.icon} strokeWidth={2.75} aria-hidden />
      {size === "sm" ? t("short") : t("badge")}
    </span>
  )
}

/**
 * Envuelve el chip de un SABOR en un selector y le cuelga «Quedan pocas» en la
 * esquina, sin tocar su interior. Los selectores tienen varias formas de chip
 * (con foto, stepper, fila) y cada una arma su propio contenido: colgarla por fuera
 * vale para todas. `pointer-events: none` para que la etiqueta no le robe el toque
 * al chip. Sin `show`, devuelve el chip tal cual (mismo árbol de siempre).
 */
export function withLowStockTag(key: string, show: boolean, chip: React.ReactNode): React.ReactNode {
  if (!show) return chip
  return (
    <div key={key} style={{ position: "relative" }}>
      {chip}
      <span style={{ position: "absolute", top: -9, right: -6, zIndex: 2, pointerEvents: "none" }}>
        <LowStockBadge size="sm" />
      </span>
    </div>
  )
}
